import { useState, useEffect, useCallback } from "react";
import {
	simpleUploadQueue,
	UploadProgress,
	UploadFile,
} from "../service/simpleUploadQueue";

export interface UseUploadQueueReturn {
	progress: UploadProgress[];
	isUploading: boolean;
	isVisible: boolean;
	totalFiles: number;
	completedFiles: number;
	failedFiles: number;
	addFiles: (files: File[]) => void;
	startUploads: () => Promise<{ uploadedItems: any[] }>;
	cancelFile: (fileId: string) => void;
	cancelAll: () => void;
	retryFile: (fileId: string) => Promise<void>;
	clearCompleted: () => void;
	clearQueue: () => void;
	hideIndicator: () => void;
	showIndicator: () => void;
}

export const useUploadQueue = (): UseUploadQueueReturn => {
	const [progress, setProgress] = useState<UploadProgress[]>([]);
	const [isVisible, setIsVisible] = useState(false);

	const queueState = simpleUploadQueue.getQueueState();
	const isUploading = queueState.isUploading;

	// Subscribe to progress updates
	useEffect(() => {
		const unsubscribeProgress = simpleUploadQueue.onProgress((newProgress) => {
			setProgress(newProgress);

			// Show indicator when there are files
			if (newProgress.length > 0 && !isVisible) {
				setIsVisible(true);
			}
		});

		const unsubscribeComplete = simpleUploadQueue.onComplete((results) => {
			// Handle completion results if needed
			console.log("Upload completed:", results);
		});

		// Initial progress update
		setProgress(simpleUploadQueue.getProgress());

		return () => {
			unsubscribeProgress();
			unsubscribeComplete();
		};
	}, [isVisible]);

	// Derived values
	const totalFiles = progress.length;
	const completedFiles = progress.filter(
		(p) => p.status === "completed"
	).length;
	const failedFiles = progress.filter((p) => p.status === "failed").length;

	// Actions
	const addFiles = useCallback((files: File[]) => {
		simpleUploadQueue.addFiles(files);
		setIsVisible(true);
	}, []);

	const startUploads = useCallback(async () => {
		return await simpleUploadQueue.startUploads();
	}, []);

	const cancelFile = useCallback((fileId: string) => {
		simpleUploadQueue.cancelFile(fileId);
	}, []);

	const cancelAll = useCallback(() => {
		simpleUploadQueue.cancelAll();
	}, []);

	const retryFile = useCallback(async (fileId: string) => {
		await simpleUploadQueue.retryFile(fileId);
	}, []);

	const clearCompleted = useCallback(() => {
		simpleUploadQueue.clearCompleted();
	}, []);

	const clearQueue = useCallback(() => {
		simpleUploadQueue.clearQueue();
		setIsVisible(false);
	}, []);

	const hideIndicator = useCallback(() => {
		setIsVisible(false);
	}, []);

	const showIndicator = useCallback(() => {
		if (progress.length > 0) {
			setIsVisible(true);
		}
	}, [progress.length]);

	return {
		progress,
		isUploading,
		isVisible,
		totalFiles,
		completedFiles,
		failedFiles,
		addFiles,
		startUploads,
		cancelFile,
		cancelAll,
		retryFile,
		clearCompleted,
		clearQueue,
		hideIndicator,
		showIndicator,
	};
};
